import { extent, max, min, bisector } from 'd3-array';
import { scaleTime, scaleLinear } from '@visx/scale';
import type { PriceEntity } from '@components/PriceChart/utils/types';
import type { ScaleTime, ScaleLinear } from 'd3-scale';

interface XStock {
  (n: PriceEntity | null): Date;
}

interface YStock {
  (n: PriceEntity | null): number | null;
}

export const bisectDate = bisector((d: PriceEntity) => {
  try {
    return new Date(d.date);
  } catch (e) {
    //
    console.warn('bisectDate: d.date is undefined');
    return new Date(0);
  }
}).right;

export const xStock: XStock = (d) => {
  if (!d) {
    console.warn('xStock: d is undefined');
    return new Date(0);
  }
  try {
    return new Date(d.date);
  } catch (e) {
    console.warn('xStock: d.date is undefined');
    return new Date(0);
  }
};

export const yStock: YStock = (d) => {
  if (!d) {
    return null;
  }
  try {
    return d.close;
  } catch (e) {
    return null;
  }
};

export const getScaleX = (
  data: PriceEntity[],
  width: number
): ScaleTime<number, number> => {
  const [a, b] = extent(data, xStock);
  return scaleTime({
    range: [0, width],
    domain: a && b ? [a, b] : undefined,
  });
};

export const getScaleY = (
  height: number,
  min: number,
  max: number
): ScaleLinear<number, number> => {
  return scaleLinear({
    range: [height, 0],
    domain: [min, max],
    nice: true,
  });
};

export const getDataSlicedByPeriod = (
  data: PriceEntity[],
  dx: number,
  xScale: ScaleTime<number, number>,
  width: number
): PriceEntity[] => {
  //	Get the first and last date objects on the xScale associated the current period's range (from nextDx to the lastX position visible)
  const periodStart = xScale.invert(dx);
  const periodEnd = xScale.invert(dx + width);
  //	bisectDate uses a right bisector, so to allow access to the most left node we use dx
  const periodStartIndex = dx === 0 ? 0 : bisectDate(data, periodStart);
  const periodEndIndex = bisectDate(data, periodEnd);
  //	Using the date objects, bisect the data to get the indexes so we can get that slice of the data
  return data.slice(periodStartIndex, periodEndIndex);
};

interface DerivedStateUpdateProps {
  dx: number;
  now: number;
  data: PriceEntity[];
  width: number;
  reset: boolean;
  xScale?: ScaleTime<number, number>;
  period: number;
  height: number;
  selectedDate?: Date;
  selectedClose?: number | null;
}

interface DerivedStateUpdate {
  dx: number;
  yMin?: number;
  yMax?: number;
  yScale: ScaleLinear<number, number>;
  xScale: ScaleTime<number, number>;
  startX: number;
  isZooming: boolean;
  selectedX: number | null;
  selectedY: number | null;
  xScaleWidth: number;
  selectedDate: Date;
  selectedClose: number | null;
  isPositiveTrend: boolean;
  priceChange: number;
  firstPrice: number;
  lastPrice: number;
}

export const getDerivedStateUpdate = (
  props: DerivedStateUpdateProps
): DerivedStateUpdate => {
  const {
    dx,
    now,
    data,
    width,
    reset,
    xScale,
    period,
    height,
    selectedDate,
    selectedClose,
  } = props;
  //
  const dateDiff = data[data.length - 1].date - data[0].date;
  const periodDiff = now - period;
  const multiplier = dateDiff / periodDiff;
  //	Prime the scale of the chart based on the period and width of the viewport
  const xScaleWidth = width * (multiplier < 1 ? 1 : multiplier);
  //	Get the xScale using the supplied data and the calculated xScaleWidth
  const nextXScale = getScaleX(data, xScaleWidth);
  //	Set the deltaX (dx)
  let nextDx = xScaleWidth - width;
  //
  if (xScale !== undefined && reset === false) {
    //	Store nextDx as maxWidth

    const maxWidth = nextDx;
    //	The current dragging/hover mouse position relative to the current dx
    const mouseDiff =
      ((dx +
        (xScale(selectedDate || xStock(data[data.length - 1])) as number)) /
        width) *
      width;
    //	Block over running bounds after subtracting mouseDiff
    nextDx =
      (nextXScale(selectedDate || xStock(data[data.length - 1])) as number) -
      mouseDiff;
    nextDx = nextDx < 0 ? 0 : nextDx;
    nextDx = nextDx > maxWidth ? maxWidth : nextDx;
  }
  //	Using the date objects, bisect the data to get the indexes so we can get that slice of the data
  const currentDataSlice = getDataSlicedByPeriod(
    data,
    nextDx,
    nextXScale,
    width
  );
  //	Using the data slice, get the min and max prices from the Y axis
  const nextYMin = min(currentDataSlice, yStock);
  const nextYMax = max(currentDataSlice, yStock);
  //	Using the min and max, resync the yScale so it can show the volatity for the data slice
  const nextYScale = getScaleY(height, nextYMin || 0, nextYMax || 0);
  //
  const nextSelectedDate = reset
    ? xStock(data[data.length - 1])
    : selectedDate || xStock(data[data.length - 1]);
  const nextSelectedClose = reset
    ? yStock(data[data.length - 1])
    : selectedClose || yStock(data[data.length - 1]);
  //
  const nextSelectedX = nextXScale(nextSelectedDate) as number;
  const nextSelectedY =
    nextSelectedClose !== null
      ? (nextYScale(nextSelectedClose) as number)
      : nextSelectedClose;

  const firstItem = currentDataSlice[0]?.close ?? 0;
  const lastItem = currentDataSlice[currentDataSlice.length - 1]?.close ?? 0;
  const firstPrice = firstItem;
  const lastPrice = lastItem;
  const priceChange = lastItem - firstItem;
  const isPositiveTrend = priceChange >= 0;

  return {
    dx: -nextDx,
    yMin: nextYMin,
    yMax: nextYMax,
    yScale: nextYScale,
    xScale: nextXScale,
    startX: 0,
    isZooming: false,
    selectedX: nextSelectedX,
    selectedY: nextSelectedY,
    xScaleWidth,
    selectedDate: nextSelectedDate,
    selectedClose: nextSelectedClose,
    isPositiveTrend,
    priceChange,
    firstPrice,
    lastPrice,
  };
};
